/**
 * Create Organization Modal
 *
 * Modal for creating a new organization when user doesn't belong to one.
 */

import { useState } from "react";
import { Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateOrganization } from "@/hooks/useOrganization";

interface CreateOrganizationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreateOrganizationModal({
  open,
  onOpenChange,
  onSuccess,
}: CreateOrganizationModalProps) {
  const [name, setName] = useState("");
  const createOrgMutation = useCreateOrganization();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    try {
      await createOrgMutation.mutateAsync({ name: name.trim() });
      setName("");
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to create organization:", error);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setName("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Create Organization
          </DialogTitle>
          <DialogDescription>
            Create a new organization to start using Vortal. You'll be the owner and can invite
            team members later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input
                id="org-name"
                placeholder="Enter organization name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                minLength={2}
                maxLength={100}
                required
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                This can be your company name or team name.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              disabled={createOrgMutation.isPending || name.trim().length < 2}
            >
              {createOrgMutation.isPending ? "Creating..." : "Create Organization"}
            </Button>
          </DialogFooter>

          {createOrgMutation.isError && (
            <p className="mt-2 text-sm text-red-600">
              {createOrgMutation.error instanceof Error
                ? createOrgMutation.error.message
                : "Failed to create organization"}
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
